import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect, ConnectedProps } from 'react-redux';
import { NavigateFunction } from 'react-router-dom';

import ReviewSubmissions, { GoToReviewType } from '../components/review/ReviewSubmissions';
import ApiClient from '../loopback/apiClient';
import {
  RootState,
  Survey,
  Trial,
  AssignedReviewSubmission,
  ReviewSubmissionData,
  AssignedReview,
  UserKeys,
  InstituteKeys,
  ReviewSurvey,
} from '../redux/types';
import ReviewSubmissionsV2 from '../components/review/ReviewSubmissionsV2';
import { fetchDigitalGroundTruthGroups } from '../redux/actions';
import { EnrollmentWithInstitute } from '../loopback/apiClient/EnrollmentApi';

type State = {
  submissions?: ReviewSubmissionData;
  enrollments?: EnrollmentWithInstitute[];
}

class ReviewSubmissionsView extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      submissions: undefined,
      enrollments: undefined,
    };
  }

  componentDidMount() {
    const { assignment } = this.props;
    this.getEnrollmentsByTrialVersion(assignment.trial, assignment.id);
  }

  componentDidUpdate(prevProps: Props) {
    const { assignment } = this.props;
    const { assignment: prevAssignment } = prevProps;
    if (assignment.id !== prevAssignment.id) {
      this.getEnrollmentsByTrialVersion(assignment.trial, assignment.id);
    }
  }

  getEnrollmentsByTrialVersion = (trial: Trial, assignedReviewId: number) => {
    const {
      locale,
      fetchDigitalGroundTruthGroups: fetchDigitalGroundTruthGroupsAction,
    } = this.props;
    if (trial.version === 2) {
      // bring new enrollments for survey 2
      // and reset submission as it is not needed and does not exist in this trial version
      this.setState({ submissions: undefined });
      ApiClient.getEnrollmentsWithInstitute(trial.id)
        .then((e) => e.filter((en) => en.status === 'approved'))
        .then((e) => {
          const containsNoSampleset = e.some(
            (enrollment) => enrollment.sampleset === null || enrollment.sampleset === undefined,
          );
          if (containsNoSampleset) {
            return e;
          }
          return e.sort((a, b) => {
            if (a.sampleset === undefined || b.sampleset === undefined) {
              return 0;
            }
            return a.sampleset - b.sampleset;
          });
        }).then((e) => {
          this.setState({ enrollments: e });
        });
      ApiClient.getDigitalSurveyByTrialId(trial.id)
        .then((surveys) => surveys.filter((ds) => !ds.exercise))
        .then((selectedSurvey) => fetchDigitalGroundTruthGroupsAction(selectedSurvey[0].id));
    } else {
      this.fetchReviewSubmissionsToState(assignedReviewId, trial.id, locale);
    }
  };

  goToReview = (
    reviewRequestId: number,
    enrollment: AssignedReviewSubmission['enrollment'],
    trial: Trial,
    survey: Survey,
    quipUser: UserKeys,
    institute: InstituteKeys,
    reviewSurvey: ReviewSurvey,
  ): void => {
    const { navigate } = this.props;
    navigate('/review', {
      state: {
        submission: enrollment.submission,
        enrollment,
        survey,
        trial,
        reviewRequestId,
        quipUser,
        institute,
        reviewSurvey,
      },
    });
  };

  goToReviewV2 = (trialId: number, enrollmentId: number): void => {
    const { navigate } = this.props;
    navigate(`/trial/${trialId}/enrollment/${enrollmentId}/review`);
  };

  goToReviewHandler: GoToReviewType = (
    assignmentId,
    enrollment,
    reviewedTrial,
    survey,
    quipUser,
    insitute,
    reviewSurvey,
  ): void => {
    this.goToReview(
      assignmentId,
      enrollment,
      reviewedTrial,
      survey,
      quipUser,
      insitute,
      reviewSurvey,
    );
  };

  fetchReviewSubmissionsToState(assignmentId: number, trialId: number, locale: string) {
    ApiClient.getReviewRequestSubmissions(assignmentId, trialId, locale)
      .then((submissions) => this.setState({ submissions }));
  }

  render(): React.ReactNode {
    const {
      assignment,
      selectedEnrollment,
      tokenId,
      locale,
      digitalGroundTruthGroupById,
    } = this.props;
    const { submissions, enrollments } = this.state;
    const { trial } = assignment;

    const gtGroupById = Object.keys(digitalGroundTruthGroupById).length > 1
      ? digitalGroundTruthGroupById : undefined;
    return (
      <>
        <div style={{ marginTop: 15 }}>
          {
            assignment.trial.version !== 2
            && submissions
            && (
              <ReviewSubmissions
                submissions={submissions}
                trial={trial}
                assignmentId={assignment.id}
                goToReview={this.goToReviewHandler}
                selectedEnrollment={selectedEnrollment}
                token={tokenId}
                locale={locale}
              />
            )
          }
          {
            assignment.trial.version === 2
            && enrollments && (
              <ReviewSubmissionsV2
                digitalGroundTruthGroupById={gtGroupById}
                enrollments={enrollments}
                goToReview={this.goToReviewV2}
              />
            )
          }
        </div>
      </>
    );
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const mapStateToProps = (state: RootState) => ({
  userId: state.users && state.users.token && state.users.token.userId,
  tokenId: state.users && state.users.token && state.users.token.id,
  locale: state.intl.locale,
  digitalGroundTruthGroupById: state.SurveyData.digitalGroundTruthGroup.byId,
});

const dispatchProps = {
  fetchDigitalGroundTruthGroups,
};

const connector = connect(mapStateToProps, dispatchProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type PropTypes = {
  selectedEnrollment: number | '' | null;
  assignment: AssignedReview;
  navigate: NavigateFunction;
}
type Props = PropTypes & PropsFromRedux;
export default connector(ReviewSubmissionsView);
